import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import Papa from "papaparse";
import Select from "react-select";
import { Grid, Card, Form, Button, Icon, Alert } from "tabler-react";
import { NotificationManager } from "react-notifications";
import { Crud_Catalogos } from "../../herramientas/Crud_Catalogos";
import { LoadingScreen } from "../../components/LoadingScreen";
import SiteWrapper from "../../SiteWrapper.react";
import styles from "./EliminacionMasiva.module.css";

const MAX_REGISTROS = 10000;
const ESTATUS_ELIMINADO = 4;

/**
 * EliminacionMasiva (Monitoreo / Navieras)
 * -----------------------------------------------------------------------------
 * Flujo guiado en 3 pasos para eliminar (estatus 4 = ELIMINADO) movimientos de
 * monitoreo de forma masiva a partir de un CSV con IDs.
 *
 * Endpoint backend:
 *   GET /navieraMovimientos/eliminarMovimientosMasiv/{idPlataforma}/{ids}
 */
const EliminacionMasiva = () => {
  /* ----------------------------- Estado --------------------------------- */
  const [step, setStep] = useState(1);

  // Paso 1
  const [plataformas, setPlataformas] = useState([]);
  const [plataforma, setPlataforma] = useState(null); // { value, label }

  // Paso 2
  const [fileName, setFileName] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [ids, setIds] = useState([]); // IDs válidos únicos (números)
  const [previewPage, setPreviewPage] = useState(1); // página de la vista previa
  const [previewPageSize, setPreviewPageSize] = useState(5); // filas por página
  const [duplicados, setDuplicados] = useState(0);
  const [invalidas, setInvalidas] = useState([]); // líneas inválidas [{linea, valor}]
  const [csvError, setCsvError] = useState("");

  // Paso 3
  const [confirmado, setConfirmado] = useState(false);
  const [loading, setLoading] = useState(false);

  const fileInputRef = useRef(null);

  /* -------------------------- Carga de plataformas ---------------------- */
  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem("usuario"));
    if (user && user.id) {
      Crud_Catalogos(
        "plataformasUsuariosPerfiles/idUsuario/" + user.id,
        "",
        "get",
        "",
        [],
        "",
        "",
        "",
        []
      )
        .then((data) => {
          if (data) {
            const opts = data.map((p) => ({
              value: String(p.plataforma.id),
              label: p.plataforma.nombre,
            }));
            setPlataformas(opts);
          }
        })
        .catch(() =>
          NotificationManager.error("Error al cargar las plataformas", "Error")
        );
    }
  }, []);

  /* ----------------------------- Derivados ------------------------------ */
  const totalPreviewPages = Math.max(1, Math.ceil(ids.length / previewPageSize));
  const preview = useMemo(() => {
    const inicio = (previewPage - 1) * previewPageSize;
    return ids.slice(inicio, inicio + previewPageSize);
  }, [ids, previewPage, previewPageSize]);
  const previewInicioIdx = (previewPage - 1) * previewPageSize;

  const puedeAvanzarPaso1 = !!plataforma;
  const puedeAvanzarPaso2 = ids.length > 0 && !csvError;
  const puedeConfirmar = confirmado && puedeAvanzarPaso2 && !loading;

  /* --------------------------- Parseo de CSV ---------------------------- */
  const procesarArchivo = useCallback((file) => {
    setCsvError("");
    setIds([]);
    setPreviewPage(1);
    setPreviewPageSize(5);
    setDuplicados(0);
    setInvalidas([]);

    if (!file) return;

    const extension = (file.name.split(".").pop() || "").toLowerCase();
    if (extension !== "csv") {
      setCsvError("El archivo no es válido. Debe ser un .csv");
      NotificationManager.warning("Selecciona un archivo .csv", "Archivo inválido");
      return;
    }

    setFileName(file.name);
    setParsing(true);

    const reader = new FileReader();
    reader.onload = ({ target }) => {
      try {
        const resultado = Papa.parse(target.result, {
          header: true,
          skipEmptyLines: true,
        });
        const filas = resultado?.data || [];

        if (filas.length === 0) {
          setCsvError("El archivo está vacío.");
          setParsing(false);
          return;
        }

        // Detectar la columna de id (id, ID, id_movimiento, etc.)
        const columnas = Object.keys(filas[0] || {});
        const columnaId =
          columnas.find((c) => c.trim().toLowerCase() === "id") ||
          columnas.find((c) =>
            c.trim().toLowerCase().replace(/[\s_]/g, "").includes("idmovimiento")
          ) ||
          columnas[0];

        if (filas.length > MAX_REGISTROS) {
          setCsvError(
            `El archivo excede el máximo de ${MAX_REGISTROS.toLocaleString()} registros (tiene ${filas.length.toLocaleString()}).`
          );
          setParsing(false);
          return;
        }

        const vistos = new Set();
        const validos = [];
        const invalidasTmp = [];
        let dupCount = 0;

        filas.forEach((fila, index) => {
          const raw = (fila[columnaId] ?? "").toString().trim();
          if (raw === "") {
            invalidasTmp.push({ linea: index + 2, valor: "(vacío)" });
            return;
          }
          if (!/^\d+$/.test(raw)) {
            invalidasTmp.push({ linea: index + 2, valor: raw });
            return;
          }
          const num = Number(raw);
          if (vistos.has(num)) {
            dupCount += 1;
            return;
          }
          vistos.add(num);
          validos.push(num);
        });

        setIds(validos);
        setDuplicados(dupCount);
        setInvalidas(invalidasTmp);

        if (validos.length === 0) {
          setCsvError("No se detectó ningún ID válido en el archivo.");
        }
      } catch (e) {
        setCsvError("No se pudo leer el archivo CSV.");
      } finally {
        setParsing(false);
      }
    };
    reader.onerror = () => {
      setCsvError("Ocurrió un error al leer el archivo.");
      setParsing(false);
    };
    reader.readAsText(file);
  }, []);

  /* ----------------------------- Drag & Drop ---------------------------- */
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    procesarArchivo(file);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleFileInput = (e) => {
    const file = e.target.files && e.target.files[0];
    procesarArchivo(file);
  };
  const abrirSelector = () => fileInputRef.current && fileInputRef.current.click();
  const onZoneKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      abrirSelector();
    }
  };

  const limpiarArchivo = () => {
    setFileName("");
    setIds([]);
    setPreviewPage(1);
    setDuplicados(0);
    setInvalidas([]);
    setCsvError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  /* ------------------------- Descargar plantilla ------------------------ */
  const descargarPlantilla = () => {
    // CSV de ejemplo: encabezado "id" + IDs de muestra (uno por línea).
    const contenido =
      "id\n" + ["1001", "1002", "1003", "1004", "1005"].join("\n") + "\n";
    // BOM para que Excel respete UTF-8 y no rompa acentos.
    const blob = new Blob(["\uFEFF" + contenido], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "plantilla_eliminacion_masiva.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /* ---------------------------- Confirmación ---------------------------- */
  const ejecutarEliminacion = () => {
    if (!puedeConfirmar) return;
    setLoading(true);

    const idsStr = ids.join(",");
    Crud_Catalogos(
      "navieraMovimientos/eliminarMovimientosMasiv/" + plataforma.value + "/" + idsStr,
      "movimientos",
      "get",
      "",
      [],
      "",
      "",
      "",
      []
    )
      .then((res) => {
        const afectados = Number.isFinite(Number(res)) ? Number(res) : ids.length;
        const noEncontrados = ids.length - afectados;
        NotificationManager.success(
          afectados + " movimiento(s) eliminado(s) correctamente",
          "Eliminación completada"
        );
        if (noEncontrados > 0) {
          NotificationManager.warning(
            noEncontrados +
              " movimiento(s) no se encontraron en esta plataforma y no se modificaron.",
            "Aviso"
          );
        }
        reiniciar();
      })
      .catch(() =>
        NotificationManager.error(
          "Ocurrió un error al eliminar los movimientos",
          "Error"
        )
      )
      .finally(() => setLoading(false));
  };

  const reiniciar = () => {
    setStep(1);
    setPlataforma(null);
    setConfirmado(false);
    limpiarArchivo();
  };

  /* ------------------------- Indicador de pasos ------------------------- */
  const Stepper = () => {
    const pasos = [
      { n: 1, label: "Plataforma" },
      { n: 2, label: "Cargar CSV" },
      { n: 3, label: "Confirmar" },
    ];
    return (
      <div className={styles.stepper} role="list" aria-label="Progreso">
        {pasos.map((p, i) => (
          <React.Fragment key={p.n}>
            <div
              role="listitem"
              aria-current={step === p.n ? "step" : undefined}
              className={
                styles.step +
                " " +
                (step === p.n ? styles.stepActive : step > p.n ? styles.stepDone : "")
              }
            >
              <span className={styles.stepCircle}>
                {step > p.n ? <Icon name="check" /> : p.n}
              </span>
              <span className={styles.stepLabel}>{p.label}</span>
            </div>
            {i < pasos.length - 1 && (
              <span
                className={
                  styles.stepConnector + " " + (step > p.n ? styles.stepConnectorDone : "")
                }
                aria-hidden="true"
              />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  /* -------------------------------- Render ------------------------------ */
  return (
    <SiteWrapper>
      <div className="container my-4">
        <Grid.Row>
          <Grid.Col lg={8} md={10} sm={12} className="mx-auto">
            <Card>
              <Card.Header>
                <Card.Title>
                  <Icon name="trash-2" className="mr-2 text-muted" />
                  Eliminación Masiva de Movimientos
                </Card.Title>
              </Card.Header>

              <Card.Body>
                <Stepper />

                {/* =============================== PASO 1 =============================== */}
                {step === 1 && (
                  <div className={styles.section}>
                    <h4 className={styles.sectionTitle}>Paso 1 · Seleccionar plataforma</h4>
                    <Form.Group
                      label={
                        <span>
                          Plataforma de origen <span className="text-danger">*</span>
                        </span>
                      }
                    >
                      <label htmlFor="plataforma-select" className="sr-only">
                        Plataforma de origen
                      </label>
                      <Select
                        inputId="plataforma-select"
                        aria-label="Plataforma de origen"
                        placeholder="Selecciona una plataforma..."
                        options={plataformas}
                        value={plataforma}
                        onChange={(opt) => setPlataforma(opt)}
                        noOptionsMessage={() => "Sin plataformas disponibles"}
                      />
                      <small className={styles.help}>
                        Selecciona la plataforma cuyos movimientos serán eliminados.
                      </small>
                    </Form.Group>

                    <div className={styles.actions}>
                      <Button
                        color="primary"
                        disabled={!puedeAvanzarPaso1}
                        onClick={() => setStep(2)}
                      >
                        Continuar <Icon name="arrow-right" className="ml-1" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* =============================== PASO 2 =============================== */}
                {step === 2 && (
                  <div className={styles.section}>
                    <h4 className={styles.sectionTitle}>Paso 2 · Cargar archivo CSV</h4>

                    <div className={styles.templateBox}>
                      <div className={styles.templateInfo}>
                        <Icon name="info" className={styles.templateIcon} />
                        <div>
                          <strong>¿No sabes cómo debe ser el archivo?</strong>
                          <div className={styles.help}>
                            El CSV debe tener una columna llamada <code>id</code> y un ID de
                            movimiento por línea. Descarga la plantilla de ejemplo:
                          </div>
                        </div>
                      </div>
                      <Button
                        color="secondary"
                        size="sm"
                        onClick={descargarPlantilla}
                        aria-label="Descargar plantilla CSV de ejemplo"
                      >
                        <Icon name="download" className="mr-1" /> Descargar plantilla
                      </Button>
                    </div>

                    <div
                      className={
                        styles.dropzone + " " + (isDragging ? styles.dropzoneActive : "")
                      }
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onClick={abrirSelector}
                      onKeyDown={onZoneKeyDown}
                      role="button"
                      tabIndex={0}
                      aria-label="Zona para arrastrar y soltar el archivo CSV, o presiona Enter para seleccionarlo"
                    >
                      <Icon name="upload-cloud" className={styles.dropIcon} />
                      <div className={styles.dropText}>
                        <strong>Arrastra y suelta</strong> tu archivo aquí
                        <br />
                        o <span className={styles.link}>haz clic para seleccionarlo</span>
                      </div>
                      <small className={styles.help}>
                        Formato: columna <code>id</code> (un ID por línea) · Máx{" "}
                        {MAX_REGISTROS.toLocaleString()} registros
                      </small>
                      <input
                        ref={fileInputRef}
                        id="csvInput"
                        name="file"
                        type="file"
                        accept=".csv"
                        onChange={handleFileInput}
                        className={styles.hiddenInput}
                      />
                    </div>

                    {fileName && (
                      <div className={styles.fileChip}>
                        <Icon name="file-text" className="mr-2" />
                        <span className={styles.fileName}>{fileName}</span>
                        <button
                          type="button"
                          className={styles.chipRemove}
                          onClick={limpiarArchivo}
                          aria-label="Quitar archivo"
                        >
                          <Icon name="x" />
                        </button>
                      </div>
                    )}

                    {parsing && (
                      <div className={styles.help}>
                        <i className="fa fa-spinner fa-spin mr-1" /> Analizando archivo...
                      </div>
                    )}

                    {/* Validaciones en tiempo real */}
                    {csvError && (
                      <Alert type="danger" icon="alert-triangle">
                        {csvError}
                      </Alert>
                    )}

                    {!csvError && ids.length > 0 && (
                      <Alert type="success" icon="check">
                        {ids.length.toLocaleString()} movimiento(s) detectado(s)
                      </Alert>
                    )}

                    {duplicados > 0 && (
                      <Alert type="warning" icon="copy">
                        {duplicados} ID(s) repetido(s) detectado(s) — se contarán una sola vez.
                      </Alert>
                    )}

                    {invalidas.length > 0 && (
                      <Alert type="warning" icon="alert-circle">
                        {invalidas.length} línea(s) inválida(s) (se ignorarán):{" "}
                        {invalidas
                          .slice(0, 5)
                          .map((x) => `L${x.linea}: "${x.valor}"`)
                          .join(", ")}
                        {invalidas.length > 5 ? "…" : ""}
                      </Alert>
                    )}

                    {/* Preview */}
                    {ids.length > 0 && (
                      <div className={styles.previewWrap}>
                        <div className={styles.previewHeader}>
                          <span>
                            Vista previa · mostrando {previewInicioIdx + 1}–
                            {Math.min(previewInicioIdx + previewPageSize, ids.length)} de{" "}
                            {ids.length.toLocaleString()}
                          </span>
                          <span className={styles.pageSizeWrap}>
                            <label htmlFor="pageSize" className={styles.pageSizeLabel}>
                              Filas:
                            </label>
                            <select
                              id="pageSize"
                              className={styles.pageSizeSelect}
                              value={previewPageSize}
                              onChange={(e) => {
                                setPreviewPageSize(Number(e.target.value));
                                setPreviewPage(1);
                              }}
                            >
                              <option value={5}>5</option>
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                            </select>
                          </span>
                        </div>
                        <div className={styles.previewScroll}>
                          <table className={styles.previewTable}>
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>ID de movimiento</th>
                              </tr>
                            </thead>
                            <tbody>
                              {preview.map((id, i) => (
                                <tr key={id}>
                                  <td>{previewInicioIdx + i + 1}</td>
                                  <td>{id}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {totalPreviewPages > 1 && (
                          <div className={styles.pagination}>
                            <button
                              type="button"
                              className={styles.pageBtn}
                              onClick={() => setPreviewPage(1)}
                              disabled={previewPage === 1}
                              aria-label="Primera página"
                            >
                              <Icon name="chevrons-left" />
                            </button>
                            <button
                              type="button"
                              className={styles.pageBtn}
                              onClick={() => setPreviewPage((p) => Math.max(1, p - 1))}
                              disabled={previewPage === 1}
                              aria-label="Página anterior"
                            >
                              <Icon name="chevron-left" />
                            </button>
                            <span className={styles.pageInfo}>
                              Página{" "}
                              <input
                                type="number"
                                className={styles.pageInput}
                                min={1}
                                max={totalPreviewPages}
                                value={previewPage}
                                onChange={(e) => {
                                  const val = Number(e.target.value);
                                  if (!val) return;
                                  const clamped = Math.min(
                                    totalPreviewPages,
                                    Math.max(1, val)
                                  );
                                  setPreviewPage(clamped);
                                }}
                                aria-label="Ir a página"
                              />{" "}
                              de {totalPreviewPages}
                            </span>
                            <button
                              type="button"
                              className={styles.pageBtn}
                              onClick={() =>
                                setPreviewPage((p) => Math.min(totalPreviewPages, p + 1))
                              }
                              disabled={previewPage === totalPreviewPages}
                              aria-label="Página siguiente"
                            >
                              <Icon name="chevron-right" />
                            </button>
                            <button
                              type="button"
                              className={styles.pageBtn}
                              onClick={() => setPreviewPage(totalPreviewPages)}
                              disabled={previewPage === totalPreviewPages}
                              aria-label="Última página"
                            >
                              <Icon name="chevrons-right" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    <div className={styles.actions}>
                      <Button color="secondary" onClick={() => setStep(1)}>
                        <Icon name="arrow-left" className="mr-1" /> Atrás
                      </Button>
                      <Button
                        color="primary"
                        disabled={!puedeAvanzarPaso2}
                        onClick={() => setStep(3)}
                      >
                        Revisar <Icon name="arrow-right" className="ml-1" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* =============================== PASO 3 =============================== */}
                {step === 3 && (
                  <div className={styles.section}>
                    <h4 className={styles.sectionTitle}>Paso 3 · Revisión y confirmación</h4>

                    <div className={styles.summary}>
                      <div className={styles.summaryRow}>
                        <span className={styles.summaryLabel}>Plataforma</span>
                        <span className={styles.summaryValue}>
                          {plataforma ? plataforma.label : "—"}
                        </span>
                      </div>
                      <div className={styles.summaryRow}>
                        <span className={styles.summaryLabel}>Movimientos a eliminar</span>
                        <span className={styles.summaryValue}>
                          <strong>{ids.length.toLocaleString()}</strong>
                        </span>
                      </div>
                      <div className={styles.summaryRow}>
                        <span className={styles.summaryLabel}>Nuevo estatus</span>
                        <span className={styles.summaryValue}>ELIMINADO ({ESTATUS_ELIMINADO})</span>
                      </div>
                    </div>

                    <div className={styles.dangerBanner} role="alert">
                      <Icon name="alert-triangle" className={styles.dangerIcon} />
                      <div>
                        <strong>Esta acción es IRREVERSIBLE.</strong>
                        <div className={styles.dangerText}>
                          Los {ids.length.toLocaleString()} movimiento(s) de{" "}
                          <strong>{plataforma ? plataforma.label : ""}</strong> pasarán a estatus
                          ELIMINADO.
                        </div>
                      </div>
                    </div>

                    <label className={styles.confirmCheck} htmlFor="confirmCheck">
                      <input
                        id="confirmCheck"
                        type="checkbox"
                        checked={confirmado}
                        onChange={(e) => setConfirmado(e.target.checked)}
                      />
                      <span>He revisado los datos y confirmo la eliminación.</span>
                    </label>

                    {loading && (
                      <div className={styles.help}>
                        Procesando… si son muchos registros esto puede tardar unos segundos.
                      </div>
                    )}

                    <div className={styles.actions}>
                      <Button color="secondary" disabled={loading} onClick={() => setStep(2)}>
                        <Icon name="arrow-left" className="mr-1" /> Atrás
                      </Button>
                      <Button color="secondary" disabled={loading} onClick={reiniciar}>
                        Cancelar
                      </Button>
                      <Button
                        color="danger"
                        disabled={!puedeConfirmar}
                        onClick={ejecutarEliminacion}
                      >
                        {loading ? (
                          <span>
                            <i className="fa fa-spinner fa-spin mr-1" /> Eliminando…
                          </span>
                        ) : (
                          <span>
                            <Icon name="trash-2" className="mr-1" /> Confirmar Eliminación
                          </span>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Grid.Col>
        </Grid.Row>
      </div>
      {loading && <LoadingScreen loading={true} mensaje="Eliminando movimientos..." />}
    </SiteWrapper>
  );
};

export default EliminacionMasiva;
